import { Typography } from "@/components/shared/typography";
import { Card, CardContent, CardHeader, Switch, View } from "@/components/ui";
import { NotificationCategory, NotificationChannel } from "./lib";
import type { Icon } from "@/components/shared/icons";
import {
  Notification,
  Envelope,
  WhatsappLogo,
  MapPinArea,
  Gift,
  Receipt,
} from "@/components/shared/icons";
import { useAuthState } from "@/store/auth";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import { cn } from "@/lib/utils";
import { useUpdateClientPreferencesMutation } from "@/gqlhooks/client/useUpdateClientPreferencesMutation";
import { LoadingDialog } from "@/components/shared/loading-dialog";

export const NotificationSettings = () => {
  const notificationSettings = useAuthState(
    (state) => state.data.userInfo.notificationSettings,
  );

  const { updatePreferences, loading } = useUpdateClientPreferencesMutation();

  const handleUpdate = (
    value: NotificationChannel | NotificationCategory,
    selected: boolean,
  ) => {
    const updateNotificationSettings = {
      ...notificationSettings,
      [value]: selected,
    };
    updatePreferences(
      "automatedNotificationEnabled",
      updateNotificationSettings,
    );
  };

  return (
    <View className="flex flex-col gap-1 pt-0 md:gap-4 md:pt-4">
      <NotificationSettingToggles
        title="Notification Channels"
        description="Choose how you want to receive updates. Critical notifications such as OTPs and invoices may still be sent regardless."
        items={[
          {
            label: "Push Notification",
            value: NotificationChannel.PUSH_NOTIF,
            icon: Notification,
            selected: notificationSettings.PUSH_NOTIF,
          },
          {
            label: "WhatsApp",
            value: NotificationChannel.WHATSAPP,
            icon: WhatsappLogo,
            selected: notificationSettings.WHATSAPP,
          },
          {
            label: "Email",
            value: NotificationChannel.EMAIL,
            icon: Envelope,
            selected: notificationSettings.EMAIL,
          },
        ]}
        onChange={handleUpdate}
      />

      <NotificationSettingToggles
        title="Notification Categories"
        description="Select the types of updates you care about. Manage what kind of messages you want to receive from us."
        items={[
          {
            label: "Visit Reminder",
            value: NotificationCategory.VISIT_REMINDER,
            icon: MapPinArea,
            selected: notificationSettings.VISIT_REMINDER,
            description:
              "Get timely updates about your bookings, service schedules, and follow-ups to keep you informed and prepared.",
          },
          {
            label: "Promo and Discount",
            value: NotificationCategory.PROMO_AND_DISCOUNT,
            icon: Gift,
            selected: notificationSettings.PROMO_AND_DISCOUNT,
            description:
              "Be the first to know about exclusive offers, discounts, and special promotions tailored just for you.",
          },
          {
            label: "Billing Notification",
            value: NotificationCategory.BILLING,
            icon: Receipt,
            selected: true,
            description:
              "Receive timely payment reminders, billing confirmations, and invoice updates to help you stay on top of your payments.",
          },
        ]}
        onChange={handleUpdate}
      />
      <LoadingDialog open={loading} />
    </View>
  );
};

const NotificationSettingToggles = ({
  title,
  description,
  items,
  onChange,
}: {
  title: string;
  description: string;
  items: {
    label: string;
    value: NotificationChannel | NotificationCategory;
    selected: boolean;
    description?: string;
    icon: Icon;
  }[];
  onChange: (
    value: NotificationChannel | NotificationCategory,
    selected: boolean,
  ) => void;
}) => {
  const { isMobile } = useWindowDimensions();

  return (
    <Card radius={isMobile ? "none" : "xxl"}>
      <CardHeader className="flex flex-col gap-2 border-b border-secondary-border md:border-b-0">
        <Typography variant="label-sm">{title}</Typography>
        <Typography variant="body-sm" color="foreground-intermediate">
          {description}
        </Typography>
      </CardHeader>
      <CardContent className="flex flex-col gap-1 py-0">
        <View className="flex flex-col gap-1">
          {items.map(({ icon: IconComponent, ...item }, index) => (
            <View
              key={item.value}
              className={cn(
                "flex flex-row items-center justify-between gap-4 py-4",
                index < items.length - 1 && "border-b border-secondary-border",
              )}
            >
              <View className="flex flex-1 flex-col gap-2">
                <View className="flex flex-row items-center gap-1">
                  <IconComponent
                    size={20}
                    weight="fill"
                    className="size-5 color-primary"
                  />
                  <Typography variant="label-sm">{item.label}</Typography>
                </View>
                {item.description && (
                  <Typography variant="body-sm" color="foreground-intermediate">
                    {item.description}
                  </Typography>
                )}
              </View>
              <Switch
                checked={item.selected}
                onCheckedChange={(checked) => onChange(item.value, checked)}
                disabled={item.value === NotificationCategory.BILLING}
              />
            </View>
          ))}
        </View>
      </CardContent>
    </Card>
  );
};
